import { useEffect } from "react";
import { DataTable } from "./DataTable";
import NotFoundPage from "../pages/NotFoundPage";
import {
  dashboardService,
  membershipService,
  membershipDataMap,
} from "../services/index";
import LoadingImage from "./LoadingImage.js";
import { createItemStore } from "../stores/createItemStore";
import useUserStore2 from "../stores/userStore2";

const columns = [
  {
    accessorKey: "rank",
    header: "Rank",
    cell: ({ row }) => <span>{row.getValue("rank")}</span>,
  },
  {
    accessorKey: "contributorId",
    header: "Contributor ID",
    cell: ({ row }) => <span>{row.getValue("contributorId")}</span>,
  },
  {
    accessorKey: "contributorName",
    header: "Contributor Name",
    cell: ({ row }) => <span>{row.getValue("contributorName")}</span>,
  },
  // {
  //   accessorKey: "totalContributions",
  //   header: "Total Contributions",
  //   cell: ({ row }) => <span>{row.getValue("totalContributions")}</span>,
  // },
  {
    accessorKey: "points",
    header: "Points",
    cell: ({ row }) => <span>{row.getValue("points")}</span>,
  },
];

const actions = (row: any) => [];

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const dataMap = (item) => ({
  rank: item.rank,
  contributorId: item._id,
  contributorName: item.name,
  totalContributions: item.total_quantities,
  points: item.total_points,
});

let storeName = "leaderboard";
const useItems = createItemStore(
  storeName,
  (contributor) => contributor.contributorId,
  (args) => dashboardService.getLeaderboard(args[0], args[1]),
  dataMap
);

storeName = "memberships";
const useMembershipItems = createItemStore(
  storeName,
  (membership) => membership.membershipId,
  (args) => membershipService.getMembershipByCommunity(args[0]),
  membershipDataMap
);

// ========================================================

export type LeaderboardProps = {
  title?: string;
  communityId?: string;
  groupId?: string;
};

export function Leaderboard({
  title = "Leaderboard",
  communityId: propCommunityId,
  groupId: propGroupId,
}: LeaderboardProps) {
  const { user } = useUserStore2();
  const currentUserId = user?.currentProfile?.id;
  const userCommunityId = user?.currentProfile?.communityId;
  const userGroupId = user?.currentProfile?.groupId;

  // Use props if provided, otherwise fall back to user store values
  const communityId = propCommunityId || userCommunityId;
  const groupId = propGroupId || userGroupId;

  // ========================================================
  // Zustand store and page hydration
  // ========================================================
  const {
    isLoading: isLoadingMemberships,
    fetchItems: fetchMemberships,
    getSortedFiltered: getMemberships,
  } = useMembershipItems((state) => state);

  const { isLoading, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );

  // Get memberships only if we have a communityId
  const memberships = communityId
    ? getMemberships((membership) => membership.communityId === communityId)
    : [];

  const showMembershipsLoading =
    communityId && memberships.length === 0 && isLoadingMemberships;

  useEffect(() => {
    if (communityId) {
      console.log(`fetchMemberships communityId:`, communityId);
      fetchMemberships({}, [communityId]);
    }
  }, [communityId]);

  // Get data sorted by points in descending order
  const sortedData = getSortedFiltered(
    (item) => {
      // If no community filter, show all items with points > 0
      if (!communityId) return item.points > 0;

      // Otherwise filter by community membership
      return (
        item.points > 0 &&
        memberships.some(
          (membership) =>
            membership.profileId === item.contributorId &&
            membership.communityId === communityId
        )
      );
    },
    (a, b) => b.points - a.points
  );

  // Set ranks with proper handling for ties
  const data = sortedData.map((item, index, array) => {
    const prevItem = array[index - 1];
    const rank =
      prevItem && prevItem.points === item.points ? prevItem.rank : index + 1;

    return {
      ...item,
      rank,
    };
  });

  const showLoading =
    (data.length === 0 && isLoading) || showMembershipsLoading;

  useEffect(() => {
    fetchItems({}, [communityId, groupId]);
  }, [communityId, groupId]);

  // console.log("leader board data: ", data);

  return (
    <div className="p-4 md:p-8 pt-0 md:pt-0 flex flex-col">
      <div className="bg-white shadow rounded-lg md:max-w-[calc(100vw-375px)] p-4 sm:p-6 text-sm md:mt-0">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
          <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
            {title}
          </div>
        </div>
        <hr />
        {showLoading ? (
          <LoadingImage />
        ) : data.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No leaderboard data available
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={data}
            actions={actions}
            showActions={false}
            currentUserId={currentUserId}
          />
        )}
      </div>
    </div>
  );
}
